import React from 'react';
import { PublisherMetrics } from '../../api';

/**
 * Compact single-pill rendering of a publisher's overall earner rating —
 * a sample-weighted average across every timeframe that has at least one
 * stance-bearing article. Shown next to the analyst's name on article cards
 * so the user has one number to scan instead of squinting at the timeframe row.
 *
 * "N/A" appears when no timeframe has been backfilled yet (or the publisher
 * has only stance==0 articles). The {@code title} attribute spells out the
 * sample count and per-publisher caveat for users who hover.
 */
export interface PublisherOverallBadgeProps {
    metrics?: PublisherMetrics | null;
}

interface Overall {
    score: number | null;
    samples: number;
}

function sampleWeightedAverage(m: PublisherMetrics | null | undefined): Overall {
    if (!m) return { score: null, samples: 0 };
    const entries: Array<[number | undefined | null, number | undefined | null]> = [
        [m.score1h, m.sampleCount1h],
        [m.score1d, m.sampleCount1d],
        [m.score1w, m.sampleCount1w],
        [m.score2w, m.sampleCount2w],
        [m.score4w, m.sampleCount4w],
        [m.score2m, m.sampleCount2m],
        [m.score3m, m.sampleCount3m],
        [m.score4m, m.sampleCount4m],
        [m.score5m, m.sampleCount5m],
        [m.score6m, m.sampleCount6m],
    ];
    let weightSum = 0;
    let scoreSum  = 0;
    let nTotal    = 0;
    for (const [score, samples] of entries) {
        if (typeof score === 'number' && Number.isFinite(score) &&
            typeof samples === 'number' && samples > 0) {
            weightSum += samples;
            scoreSum  += score * samples;
            nTotal    += samples;
        }
    }
    if (weightSum === 0) return { score: null, samples: 0 };
    return { score: scoreSum / weightSum, samples: nTotal };
}

export const PublisherOverallBadge: React.FC<PublisherOverallBadgeProps> = ({ metrics }) => {
    const { score, samples } = sampleWeightedAverage(metrics);
    const hasScore = score != null;
    // Low-n is calculated as < 4 because the badge already aggregates across
    // up to 10 timeframes — anything below 4 means the publisher has fewer than
    // ~half a stance-bearing article per window and the average is noisy.
    const underpowered = samples < 4;

    let bg = '#e9ebed';
    let color = '#5f6b7a';
    if (hasScore) {
        if ((score ?? 0) >= 0) {
            bg    = underpowered ? '#dbeedf' : '#a7e0b8';
            color = underpowered ? '#365d44' : '#0f4d23';
        } else {
            bg    = underpowered ? '#f1dbdb' : '#eba9a9';
            color = underpowered ? '#80292a' : '#5a1212';
        }
    }

    const text = hasScore
        ? `Overall ${(score ?? 0) >= 0 ? '+' : ''}${(score ?? 0).toFixed(1)}%`
        : 'Overall N/A';
    const title = hasScore
        ? `Sample-weighted average across all 10 timeframes — ${samples} stance-bearing sample${samples === 1 ? '' : 's'} total.`
        : 'No backfilled timeframes yet — the daily metrics job will populate this once forward prices fill in.';

    return (
        <span
            title={title}
            style={{
                display: 'inline-flex',
                alignItems: 'baseline',
                gap: 4,
                padding: '2px 8px',
                borderRadius: 12,
                fontSize: 11,
                lineHeight: '16px',
                background: bg,
                color: color,
                fontFamily: 'monospace',
                fontWeight: 700,
            }}
        >
            {text}
        </span>
    );
};
